import { useEffect, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AuthContext, type AuthContextValue, type AuthStatus } from '@/lib/hooks/useAuth';
import {
  ApiClientError,
  authApi,
  oauthApi,
  onAuthEvent,
  queryKeys,
  tokenStore,
  usersApi,
  type LoginInput,
  type OAuthProvider,
  type RegisterInput,
} from '@/lib/api';
import type { UserProfile } from '@/types/api';

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const qc = useQueryClient();

  const meQuery = useQuery<UserProfile | null>({
    queryKey: queryKeys.me,
    queryFn: async () => {
      if (!tokenStore.hasRefresh() && !tokenStore.getAccessToken()) return null;
      try {
        return await usersApi.me();
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    return onAuthEvent((type) => {
      if (type === 'signed-out') {
        qc.setQueryData(queryKeys.me, null);
        qc.removeQueries({ queryKey: queryKeys.portfolio });
        qc.removeQueries({ queryKey: queryKeys.tenant });
      }
    });
  }, [qc]);

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: async (tokens) => {
      qc.setQueryData(queryKeys.me, tokens.user);
      await qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (input: RegisterInput) => authApi.register(input),
    onSuccess: async (tokens) => {
      qc.setQueryData(queryKeys.me, tokens.user);
      await qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      qc.setQueryData(queryKeys.me, null);
      qc.clear();
      toast.success('Signed out');
    },
  });

  const completeOAuthMutation = useMutation({
    mutationFn: (code: string) => oauthApi.exchange(code),
    onSuccess: async (tokens) => {
      qc.setQueryData(queryKeys.me, tokens.user);
      await qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const status: AuthStatus = meQuery.isPending
    ? 'loading'
    : meQuery.data
      ? 'authenticated'
      : 'unauthenticated';

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user: (meQuery.data as UserProfile | null | undefined) ?? null,
      login: async (input) => {
        await loginMutation.mutateAsync(input);
      },
      register: async (input) => {
        await registerMutation.mutateAsync(input);
      },
      signOut: async () => {
        await logoutMutation.mutateAsync();
      },
      signInWithProvider: (provider: OAuthProvider, returnTo?: string) => {
        oauthApi.begin(provider, returnTo);
      },
      completeOAuth: async (code: string) => {
        await completeOAuthMutation.mutateAsync(code);
      },
    }),
    [
      status,
      meQuery.data,
      loginMutation,
      registerMutation,
      logoutMutation,
      completeOAuthMutation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
